import {
  Activity,
  ArchiveRestore,
  Bell,
  BellRing,
  BookOpen,
  Bot,
  Boxes,
  Bug,
  CalendarClock,
  ChartLine,
  CircleDashed,
  Database,
  FileText,
  Gauge,
  GitBranch,
  Globe,
  HardDrive,
  Hash,
  History,
  Home,
  KeyRound,
  Layers,
  Mail,
  Megaphone,
  MessageSquare,
  Network,
  Package,
  Palette,
  Plug,
  Radar,
  Receipt,
  ScrollText,
  Send,
  Server,
  Settings,
  Shield,
  Siren,
  Sparkles,
  Store,
  Terminal,
  User,
  Users,
  Wrench,
} from 'lucide-react';
import { KanameGlyph } from '@/components/brand/KanameGlyph';
import type { FanPlate } from '@/components/brand/ServiceFan';
import type { AppInfo, MainMenuItem, MenuGroup, MenuItem } from '@/components/layout/shell/types';
import type { ResolvedPath, ShellIcon } from '@/components/layout/workspace/types';
import { plural } from '@/lib/plural';
import type { FoundationPrefs } from './prefs';
import { isSampleIncident, isSampleNode } from './screens/infra/data';

/*
  Модель страницы «Основа»: адреса, меню трёх уровней и что знает оболочка про каждый адрес.
  Данные примерные; приложение-образец — «Инфраструктура» с витрины. Логика того, что где
  открыто и как сохраняется, — дело ядра (docs/foundation.md §1); здесь — ровно столько, чтобы
  были видны вид и переходы.
*/

export const HOME = '/design/foundation';
export const at = (rest = '') => `${HOME}${rest}`;

/* ----- Уровень 1 ----- */

export const SECTIONS: MainMenuItem[] = [{ id: 'marketplace', label: 'Marketplace', icon: Store }];

export const INFRA_PIN: MainMenuItem = { id: 'app:infra', label: 'Инфраструктура', icon: Activity };

export const TOOLS = (unread: number): MainMenuItem[] => [
  { id: 'ai', label: 'ИИ', icon: Sparkles, accent: 'ai', hint: 'ИИ · Ctrl+J' },
  { id: 'notifications', label: 'Уведомления', icon: Bell, badge: unread, hint: `Уведомления · ${unread}` },
  { id: 'settings', label: 'Настройки', icon: Settings },
];

/* ----- Уровень 2: разделы платформы ----- */

export interface SectionDef {
  id: string;
  title: string;
  base: string;
  groups: MenuGroup[];
}

export const MARKETPLACE: SectionDef = {
  id: 'marketplace',
  title: 'Marketplace',
  base: '/marketplace',
  groups: [
    {
      items: [
        { key: 'all', label: 'Все модули', icon: Store },
        // Обзор «Мои сервисы» — веер сервисов (spec-4); счётчики — marketplaceGroups.
        { key: 'installed', label: 'Мои сервисы', icon: KanameGlyph },
        { key: 'recommended', label: 'Рекомендуем', icon: Sparkles },
      ],
    },
    {
      title: 'Категории',
      items: [
        { key: 'infra', label: 'Инфраструктура', icon: Wrench },
        { key: 'ai-tools', label: 'ИИ-инструменты', icon: Sparkles },
        { key: 'comm', label: 'Коммуникации', icon: Megaphone },
        { key: 'admin', label: 'Администрирование', icon: Shield },
      ],
    },
  ],
};

export const SETTINGS: SectionDef = {
  id: 'settings',
  title: 'Настройки',
  base: '/settings',
  groups: [
    {
      items: [
        { key: 'profile', label: 'Профиль', icon: User },
        { key: 'notifications', label: 'Уведомления', icon: Bell },
        { key: 'view', label: 'Вид', icon: Palette },
        { key: 'integrations', label: 'Интеграции', icon: Plug },
        { key: 'security', label: 'Безопасность', icon: Shield },
      ],
    },
    // Внизу, отдельно: что такое Kaname — мысль про заклёпку и пластины, обыгранная на знаке.
    { items: [{ key: 'about', label: 'О Kaname', icon: KanameGlyph }] },
  ],
};

export const SECTION_DEFS: Record<string, SectionDef> = { marketplace: MARKETPLACE, settings: SETTINGS };

/* ----- Уровень 3: приложение-образец ----- */

export const INFRA_APP: AppInfo = { name: 'Инфраструктура', icon: Activity };

export const INFRA_GROUPS: MenuGroup[] = [
  {
    items: [
      { key: 'overview', label: 'Обзор' },
      { key: 'problems', label: 'Проблемы', count: 12, countTone: 'danger' },
      { key: 'nodes', label: 'Узлы', count: 71 },
      { key: 'vms', label: 'Виртуальные машины' },
      { key: 'groups', label: 'Группы' },
    ],
  },
  {
    title: 'Сохранённые виды',
    items: [
      { key: 'night', label: 'Ночные алерты' },
      { key: 'dc-2', label: 'DC-2 · критичные' },
      { key: 'unacked', label: 'Без подтверждения', count: 7 },
    ],
  },
];

export const INFRA_MANAGE: MenuItem[] = [
  { key: 'manage-settings', label: 'Настройки' },
  { key: 'manage-access', label: 'Доступ' },
  { key: 'manage-journal', label: 'Журнал' },
  { key: 'manage-status', label: 'Состояние' },
];

const INFRA_LABELS: Record<string, string> = Object.fromEntries(
  [...INFRA_GROUPS.flatMap((g) => g.items), ...INFRA_MANAGE].map((i) => [i.key, i.label]),
);

/** Есть ли у приложения-образца такой вид (пункт третьего уровня). */
export const isInfraView = (key: string) => Object.hasOwn(INFRA_LABELS, key);

/** Ключ пункта третьего уровня → адрес вида. */
export const infraPath = (key: string) => (key === 'overview' ? at('/apps/infra') : at(`/apps/infra/${key}`));

/* ----- Каталог ----- */

export interface Module {
  id: string;
  name: string;
  kind: 'app' | 'adapter';
  category: string;
  description: string;
  /** Подключён (в моих сервисах). */
  installed: boolean;
  /** Подключён и включён: шлёт события. */
  active?: boolean;
  soon?: boolean;
}

/** Карточка каталога; подключён ли модуль, решают сервисы человека (withServices). */
type CatalogModule = Omit<Module, 'installed' | 'active'>;

/*
  32 карточки — только каталог, без экранов: при 30 подключённых каталог не исчерпан (VMmanager и
  «Обратные зоны» не входят в образец SERVICE_ORDER). Имена настоящих продуктов — образец, не обещание
  интеграций.
*/
export const MODULES: CatalogModule[] = [
  {
    id: 'infra',
    name: 'Инфраструктура',
    kind: 'app',
    category: 'infra',
    description:
      'Узлы, машины и проблемы в одном месте: метрики, события на графиках, обслуживание по шагам.',
  },
  {
    id: 'slack',
    name: 'Slack',
    kind: 'adapter',
    category: 'comm',
    description: 'Связь «почта ↔ пользователь Slack», события через webhook, сообщения от бота.',
  },
  {
    id: 'jira',
    name: 'Jira',
    kind: 'adapter',
    category: 'comm',
    description: 'Задачи и заявки: ссылки из любого приложения, статус рядом с именем задачи.',
    soon: true,
  },
  {
    id: 'netbox',
    name: 'NetBox',
    kind: 'adapter',
    category: 'infra',
    description: 'Площадки, стойки и адреса — источник правды для имён, которые всплывают карточками.',
    soon: true,
  },
  {
    id: 'digest',
    name: 'ИИ-сводка смены',
    kind: 'app',
    category: 'ai-tools',
    description: 'Что произошло за смену: сбои, решения, незакрытые хвосты — одной страницей к утру.',
    soon: true,
  },
  {
    id: 'access-review',
    name: 'Пересмотр доступов',
    kind: 'app',
    category: 'admin',
    description: 'Раз в квартал: кто и куда имеет доступ, что пора снять.',
    soon: true,
  },
  {
    id: 'gitlab',
    name: 'GitLab',
    kind: 'adapter',
    category: 'infra',
    description: 'Выкладки — засечками на графиках и строками в журнале узла.',
  },
  {
    id: 'grafana',
    name: 'Grafana',
    kind: 'adapter',
    category: 'infra',
    description: 'Дашборды и сайленсы рядом с узлом, без отдельной вкладки.',
  },
  {
    id: 'zabbix',
    name: 'Zabbix',
    kind: 'adapter',
    category: 'infra',
    description: 'Триггеры и история проблем — на графиках узла, рядом с новым мониторингом.',
  },
  {
    id: 'victoria-metrics',
    name: 'VictoriaMetrics',
    kind: 'adapter',
    category: 'infra',
    description: 'Метрики узлов и машин для графиков — с историей за год.',
  },
  {
    id: 'telegram',
    name: 'Telegram',
    kind: 'adapter',
    category: 'comm',
    description: 'Уведомления и короткие ответы в личном чате с ботом.',
  },
  {
    id: 'openbao',
    name: 'OpenBao',
    kind: 'adapter',
    category: 'admin',
    description: 'Секреты и ключи — по ссылке из приложения, без пересылки в чате.',
  },
  {
    id: 'proxmox',
    name: 'Proxmox VE',
    kind: 'adapter',
    category: 'infra',
    description: 'Машины, узлы и хранилища кластеров — состояние и действия из карточки.',
  },
  {
    id: 'billmanager',
    name: 'BILLmanager',
    kind: 'adapter',
    category: 'admin',
    description: 'Услуги и счета клиента — рядом с его машиной и заявками.',
  },
  {
    id: 'dnsmanager',
    name: 'DNSmanager',
    kind: 'adapter',
    category: 'infra',
    description: 'Зоны и записи: что поменялось, кем и когда.',
  },
  {
    id: 'victoria-logs',
    name: 'VictoriaLogs',
    kind: 'adapter',
    category: 'infra',
    description: 'Журналы служб и узлов — поиск от времени события в одно нажатие.',
  },
  {
    id: 'alertmanager',
    name: 'Alertmanager',
    kind: 'adapter',
    category: 'infra',
    description: 'Алерты и сайленсы: заглушить на время работ прямо из проблемы.',
  },
  {
    id: 'confluence',
    name: 'Confluence',
    kind: 'adapter',
    category: 'comm',
    description: 'Регламенты и разборы — ссылкой рядом с проблемой, к которой относятся.',
  },
  {
    id: 'incident-io',
    name: 'incident.io',
    kind: 'adapter',
    category: 'comm',
    description: 'Инцидент открывается из проблемы; ход и итог — в журнале узла.',
  },
  {
    id: 'sentry',
    name: 'Sentry',
    kind: 'adapter',
    category: 'infra',
    description: 'Ошибки приложений: сколько людей задело и после какой выкладки началось.',
  },
  {
    id: 'ai-triage',
    name: 'ИИ-разбор алертов',
    kind: 'app',
    category: 'ai-tools',
    description: 'Собирает похожие алерты в одну проблему и предлагает вероятную причину — решает дежурный.',
  },
  {
    id: 'oncall',
    name: 'Дежурства',
    kind: 'app',
    category: 'admin',
    description: 'Кто дежурит сейчас и кто следующий, замены и передача смены.',
  },
  {
    id: 'backup',
    name: 'Резервные копии',
    kind: 'app',
    category: 'infra',
    description: 'Что и когда копировалось, последние ошибки, восстановление по шагам.',
  },
  {
    id: 'capacity',
    name: 'Ёмкость и прогноз',
    kind: 'app',
    category: 'infra',
    description: 'Заполнение узлов и хранилищ с прогнозом: когда закончится место.',
  },
  {
    id: 'ansible',
    name: 'Ansible',
    kind: 'adapter',
    category: 'infra',
    description: 'Сценарии с проверкой перед запуском и журналом: кто что выполнил.',
  },
  {
    id: 's3',
    name: 'Объектное хранилище',
    kind: 'app',
    category: 'infra',
    description: 'Хранилища, квоты и ключи доступа — без отдельной панели.',
  },
  {
    id: 'email',
    name: 'Почта',
    kind: 'adapter',
    category: 'comm',
    description: 'Письма клиентам по шаблонам — с историей отправки у клиента.',
  },
  {
    id: 'audit',
    name: 'Журнал действий',
    kind: 'app',
    category: 'admin',
    description: 'Кто, что и когда сделал во всех сервисах — одной лентой с поиском.',
  },
  {
    id: 'ai-reply',
    name: 'ИИ-черновик ответа',
    kind: 'app',
    category: 'ai-tools',
    description: 'Черновик ответа на заявку по истории клиента — правит и отправляет человек.',
  },
  {
    id: 'terraform',
    name: 'Terraform',
    kind: 'adapter',
    category: 'infra',
    description: 'План изменений с разницей «было — станет»: применяется только после просмотра.',
  },
  {
    id: 'vmmanager',
    name: 'VMmanager',
    kind: 'adapter',
    category: 'infra',
    description: 'Машины и узлы панели — состояние и переезд из карточки машины.',
  },
  {
    id: 'rdns',
    name: 'Обратные зоны',
    kind: 'app',
    category: 'infra',
    description: 'Обратные записи адресов: проверка, правка списком и история изменений.',
  },
];

export const moduleById = (id: string) => MODULES.find((m) => m.id === id);

/** Значки модулей — общие для каталога, веера, карточки модуля и строк главной. */
export const MODULE_ICONS: Record<string, ShellIcon> = {
  infra: Activity,
  slack: Hash,
  jira: Plug,
  netbox: Network,
  digest: Sparkles,
  'access-review': Users,
  gitlab: GitBranch,
  grafana: Gauge,
  zabbix: Radar,
  'victoria-metrics': ChartLine,
  telegram: Send,
  openbao: KeyRound,
  proxmox: Server,
  billmanager: Receipt,
  dnsmanager: Globe,
  'victoria-logs': ScrollText,
  alertmanager: BellRing,
  confluence: BookOpen,
  'incident-io': Siren,
  sentry: Bug,
  'ai-triage': Bot,
  oncall: CalendarClock,
  backup: ArchiveRestore,
  capacity: Layers,
  ansible: Terminal,
  s3: Database,
  email: Mail,
  audit: History,
  'ai-reply': MessageSquare,
  terraform: Boxes,
  vmmanager: HardDrive,
  rdns: FileText,
};

/** Подписи категорий — из группы «Категории» меню Marketplace. */
export const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  (MARKETPLACE.groups.find((g) => g.title === 'Категории')?.items ?? []).map((i) => [i.key, i.label]),
);

/** Вид модуля словом: «Приложение» или «Подключение». */
export const kindLabel = (m: Pick<Module, 'kind'>) => (m.kind === 'app' ? 'Приложение' : 'Подключение');

/* ----- Мои сервисы: образец ----- */

/** Порядок подключения в образце: «Нет · 2 · 6 · 12 · 24 · 30» на верхней полосе — его начало. */
export const SERVICE_ORDER = [
  'infra',
  'slack',
  'jira',
  'netbox',
  'digest',
  'access-review',
  'gitlab',
  'grafana',
  'zabbix',
  'victoria-metrics',
  'telegram',
  'openbao',
  'proxmox',
  'billmanager',
  'dnsmanager',
  'victoria-logs',
  'alertmanager',
  'confluence',
  'incident-io',
  'sentry',
  'ai-triage',
  'oncall',
  'backup',
  'capacity',
  'ansible',
  's3',
  'email',
  'audit',
  'ai-reply',
  'terraform',
];

/** Выключенные в образце: из 6 — 1, из 12 — 3, из 24 — 6, из 30 — 8. */
export const SAMPLE_OFF = [
  'access-review',
  'gitlab',
  'telegram',
  'billmanager',
  'confluence',
  'oncall',
  's3',
  'terraform',
];

/** Сбой образца (переключатель «Сбой у Slack»). */
export const SAMPLE_ALERT = { id: 'slack', text: 'Webhook не отвечает 12 мин' };

/** Факты одной строкой — у сервисов, которым есть что сказать. */
export const SAMPLE_FACTS: Record<string, string> = { infra: '12 проблем · 71 узел' };

/** Каталог глазами человека: что подключено, что включено, что ещё «скоро». */
export function withServices(services: readonly string[], off: readonly string[]): Module[] {
  return MODULES.map((m) => {
    const installed = services.includes(m.id);
    return { ...m, installed, active: installed && !off.includes(m.id), soon: !!m.soon && !installed };
  });
}

export interface MyService {
  module: Module;
  /** Место в порядке подключения, с нуля. */
  index: number;
  on: boolean;
  /** Причина сбоя — только у включённого. */
  alert?: string;
}

type ServicePrefs = Pick<FoundationPrefs, 'services' | 'servicesOff' | 'serviceAlert'>;

/** Подключённые сервисы в порядке подключения. */
export function myServices(prefs: ServicePrefs): MyService[] {
  const catalog = withServices(prefs.services, prefs.servicesOff);
  return prefs.services.flatMap((id, index) => {
    const module = catalog.find((m) => m.id === id);
    if (!module) return [];
    const on = !!module.active;
    const alert = on && prefs.serviceAlert && id === SAMPLE_ALERT.id ? SAMPLE_ALERT.text : undefined;
    return [{ module, index, on, alert }];
  });
}

/** Пластины веера «Мои сервисы». */
export function fanPlates(prefs: ServicePrefs): FanPlate[] {
  return myServices(prefs).map((s) => ({ id: s.module.id, label: s.module.name, on: s.on, alert: s.alert }));
}

/** «Сервисов пока нет», «1 сервис», «3 сервиса», «12 сервисов». */
export function servicesLabel(n: number): string {
  return n === 0 ? 'Сервисов пока нет' : `${n} ${plural(n, 'сервис', 'сервиса', 'сервисов')}`;
}

/** Меню Marketplace со счётчиками: весь каталог и мои сервисы (при нуле — без счётчика). */
export function marketplaceGroups(services: readonly string[]): MenuGroup[] {
  const counts: Record<string, number | undefined> = {
    all: MODULES.length,
    installed: services.length || undefined,
  };
  return MARKETPLACE.groups.map((g) => ({
    ...g,
    items: g.items.map((i) => (i.key in counts ? { ...i, count: counts[i.key] } : i)),
  }));
}

/* ----- Что оболочка знает про адрес ----- */

/** Сервис адреса: 'home', раздел платформы или приложение `app:…`. */
export function serviceOf(path: string): string {
  return resolveFoundation(path).serviceId;
}

/** Ключ активного пункта меню для адреса (у объекта — пункт его списка). */
export function menuKeyOf(path: string): string | null {
  const r = resolveFoundation(path);
  const target = r.type === 'object' && r.homeView ? r.homeView : path;
  const rel = target.slice(HOME.length);
  const seg = rel.split('/').filter(Boolean);
  if (seg[0] === 'marketplace') return seg[1] ?? 'all';
  if (seg[0] === 'settings') return seg[1] ?? 'profile';
  if (seg[0] === 'apps') return seg[2] ?? 'overview';
  return null;
}

export function resolveFoundation(path: string): ResolvedPath {
  const rel = path.startsWith(HOME) ? path.slice(HOME.length) : path;
  const seg = rel.split('/').filter(Boolean);

  if (seg[0] === 'marketplace') {
    if (seg[1] === 'm' && seg[2]) {
      const m = moduleById(seg[2]);
      return {
        type: 'object',
        serviceId: 'marketplace',
        title: m?.name ?? 'Модуль',
        // Чего нет — у того на вкладке пунктирный круг: вкладка есть, а за ней пусто (R42).
        icon: m ? Package : CircleDashed,
        trail: `Marketplace › ${m?.name ?? 'Модуль'}`,
        homeView: at('/marketplace'),
      };
    }
    const item = MARKETPLACE.groups.flatMap((g) => g.items).find((i) => i.key === (seg[1] ?? 'all'));
    return { type: 'view', serviceId: 'marketplace', title: item?.label ?? 'Marketplace', icon: Store };
  }

  if (seg[0] === 'settings') {
    const item = SETTINGS.groups.flatMap((g) => g.items).find((i) => i.key === (seg[1] ?? 'profile'));
    return { type: 'view', serviceId: 'settings', title: item?.label ?? 'Настройки', icon: Settings };
  }

  if (seg[0] === 'apps' && seg[1] === 'infra') {
    if (seg[2] === 'node' && seg[3]) {
      return {
        type: 'object',
        serviceId: 'app:infra',
        title: seg[3],
        // На вкладках приложения — значок приложения: видно, чья вкладка.
        icon: isSampleNode(seg[3]) ? Activity : CircleDashed,
        trail: `Инфраструктура › Узлы › ${seg[3]}`,
        homeView: at('/apps/infra/nodes'),
      };
    }
    if (seg[2] === 'incident' && seg[3]) {
      return {
        type: 'object',
        serviceId: 'app:infra',
        title: seg[3],
        icon: isSampleIncident(seg[3]) ? Activity : CircleDashed,
        trail: `Инфраструктура › Проблемы › ${seg[3]}`,
        homeView: at('/apps/infra/problems'),
      };
    }
    const key = seg[2] ?? 'overview';
    if (!isInfraView(key)) {
      return {
        type: 'view',
        serviceId: 'app:infra',
        title: 'Не найдено',
        icon: CircleDashed,
        trail: 'Инфраструктура › Не найдено',
      };
    }
    return {
      type: 'view',
      serviceId: 'app:infra',
      title: INFRA_LABELS[key],
      icon: Activity,
      trail: `Инфраструктура › ${INFRA_LABELS[key]}`,
    };
  }

  if (seg.length === 0) return { type: 'view', serviceId: 'home', title: 'Главная', icon: Home };
  // Неизвестный адрес: вкладка «Не найдено». Меню ведёт себя как на главной — знак в меню новых
  // состояний не показывает (решение 21.10).
  return { type: 'view', serviceId: 'home', title: 'Не найдено', icon: CircleDashed };
}
